import {
  canAccessUsers,
  canAccessStaff,
  canAccessBranches,
  canAccessVehicles,
  canAccessDesignations,
  canAccessDepartments,
  canAccessSubDepartments,
  isCorporateAdmin,
} from '../../utils/roleAccess';

/**
 * Settings landing cards (Zoho Books-style grid).
 * Visibility uses the same role rules as the former Settings sidebar items.
 */
export const SETTINGS_SECTIONS = [
  {
    id: 'my-account',
    title: 'My Account',
    icon: 'fas fa-user',
    items: [
      {
        id: 'edit-profile',
        label: 'Edit Profile',
        to: '/edit-profile',
        alwaysVisible: true,
      },
      {
        id: 'change-password',
        label: 'Change Password',
        to: '/changePassword',
        alwaysVisible: true,
      },
    ],
  },
  {
    id: 'users',
    title: 'Users',
    icon: 'fas fa-users',
    items: [
      {
        id: 'add-user',
        label: 'Add New User',
        to: '/settings/adduser',
        menu_key: 'settings_users',
        canAccess: (role) => canAccessUsers(role),
      },
      {
        id: 'manage-users',
        label: 'Manage Users',
        to: '/settings/userlist',
        menu_key: 'settings_users',
        canAccess: (role) => canAccessUsers(role),
      },
      {
        id: 'access-control',
        label: 'Access Control',
        to: '/settings/accessControl',
        menu_key: 'settings_access_control',
        canAccess: (role) => isCorporateAdmin(role),
      },
    ],
  },
  {
    id: 'corporate',
    title: 'Corporate',
    icon: 'fas fa-building',
    items: [
      {
        id: 'branches',
        label: 'Branches',
        to: '/settings/branchList',
        menu_key: 'settings_branches',
        feature_key: 'branch',
        canAccess: (role) => canAccessBranches(role),
      },
      {
        id: 'departments',
        label: 'Departments',
        to: '/settings/departmentList',
        menu_key: 'settings_departments',
        feature_key: 'department',
        canAccess: (role) => canAccessDepartments(role),
      },
      {
        id: 'sub-departments',
        label: 'Sub Departments',
        to: '/settings/subDepartmentList',
        menu_key: 'settings_sub_departments',
        feature_key: 'department',
        canAccess: (role) => canAccessSubDepartments(role),
      },
      {
        id: 'vault-passwords',
        label: 'Vault passwords',
        to: '/credential-vault/vaultSettings',
        menu_key: 'credential_vault_settings',
        canAccess: () => true,
      },
    ],
  },
  {
    id: 'hr',
    title: 'HR',
    icon: 'fas fa-id-badge',
    items: [
      {
        id: 'staff',
        label: 'Staff',
        to: '/staff/staffList',
        menu_key: 'settings_staff',
        canAccess: (role) => canAccessStaff(role),
      },
      {
        id: 'vehicle',
        label: 'Vehicle',
        to: '/vehicle/vehicleList',
        menu_key: 'settings_vehicle',
        canAccess: (role) => canAccessVehicles(role),
      },
      {
        id: 'designations',
        label: 'Designations',
        to: '/settings/designationList',
        menu_key: 'settings_designations',
        feature_key: 'department',
        canAccess: (role) => canAccessDesignations(role),
      },
    ],
  },
  {
    id: 'billing',
    title: 'Billing',
    icon: 'fas fa-file-invoice-dollar',
    items: [
      {
        id: 'subscriptions',
        label: 'Subscriptions',
        to: '/user-subscription',
        alwaysVisible: true,
      },
      {
        id: 'credit-wallet',
        label: 'Credit Wallet',
        to: '/credits',
        alwaysVisible: true,
      },
    ],
  },
  {
    id: 'snap-bills',
    title: 'Snap Bills',
    icon: 'fas fa-file-invoice',
    items: [
      {
        id: 'snap-bill-config',
        label: 'Configurations',
        to: '/settings/snapBill',
        menu_key: 'invoice_scanner_access',
        feature_key: 'invoice_scanner',
        // Hide entirely when corporate/user has no Snap Bills access (do not show locked link)
        hide_when_unavailable: true,
      },
    ],
  },
];

/**
 * @param {string|null} userRole
 * @param {string[]|null} menuAccessKeys
 * @param {{ featureAvailability?: Record<string, boolean> }} [options]
 *   featureAvailability: feature_key → true if corporate/user has access.
 *   Items with hide_when_unavailable are omitted when availability is false.
 */
export function filterSettingsSections(userRole, menuAccessKeys, options = {}) {
  const hasMenuGate = Array.isArray(menuAccessKeys);
  const featureAvailability = options.featureAvailability || {};

  return SETTINGS_SECTIONS.map((section) => {
    const items = (section.items || []).filter((item) => {
      if (
        item.hide_when_unavailable &&
        item.feature_key &&
        featureAvailability[item.feature_key] === false
      ) {
        return false;
      }

      if (item.alwaysVisible) return true;

      if (hasMenuGate) {
        if (!item.menu_key) return true;
        return menuAccessKeys.includes(item.menu_key);
      }

      if (typeof item.canAccess === 'function') {
        return item.canAccess(userRole);
      }

      return true;
    });

    return { ...section, items };
  }).filter((section) => section.items.length > 0);
}
